/**
 * Save a stage's zone for a STAGE, and undo that save — the client side of
 * the `upsert_stage_zone_for_stage` RPC (migration 20261021130200).
 *
 * A producer redraws "Mainstage", not the row `day1_stage3`. The RPC takes
 * the stage (a briefing_stages id) and a scope:
 *
 *   • "all_days" — the outline goes onto every festival day that has a
 *     stage with the same label, in one transaction;
 *   • "day"      — only that stage's day.
 *
 * On every day after day 1 that a save writes — either scope — the server
 * first copies day 1's outlines of the OTHER stages that have none there
 * yet, so a crew build still on the old "own zones, else day 1's" map rule
 * never loses them.
 *
 * Either way the server keeps each zone's id (and its presence history),
 * sends the crew ONE refresh push, and answers with `previous`: what each
 * written row held before. Hand that to `restoreStageZones` and the whole
 * save is undone in one call — copies included, and a stage-less zone the
 * save took over gets its old name back and loses the stage again. Two
 * things an undo leaves as the save made them, reported in `skippedIds`
 * while the rest of the undo still runs: an outline the server's polygon
 * rules refuse (a legacy shape from before them), and a zone the save
 * created that crew presence has since been logged in (on a festival day
 * that is minutes — removing it would erase that history).
 *
 * The polygon is checked here first with the same rules the server
 * enforces (validateZonePolygon ⇄ the festival_areas_validate_polygon
 * trigger), so a shape the server would refuse never costs a round trip;
 * a server refusal of the shape comes back as the same typed error.
 *
 * Results are a tagged union instead of a throw: the caller (a map screen
 * mid-gesture) has to show something specific for a bad shape, and must
 * not lose the drawing to an exception it forgot to catch. Narrow with
 * `result.ok === false` — web and mobile compile without strictNullChecks,
 * where `!result.ok` does not narrow.
 */

import { z } from "zod";
import {
  ZONE_POLYGON_REJECTIONS,
  validateZonePolygon,
  zoneGeometryOf,
  type ZonePolygonRejection,
} from "./geo/polygonValidity";

// deno-lint-ignore no-explicit-any
type Client = any; // kept loose so both web and mobile Supabase clients work

/** Which days one save covers. */
export type StageZoneScope = "all_days" | "day";

/**
 * One row of an undo: the zone `area_id` gets `polygon` back, or — when
 * `polygon` is null — is removed again (it did not exist before the save).
 * A zone with presence history is never removed (see `skippedIds`).
 */
export type StageZoneRestoreEntry = {
  area_id: string;
  polygon: Record<string, unknown> | null;
  /**
   * Only when the save took over a zone that had no stage (an orphan on
   * the stage's slot): the stage link and name it had, put back with it.
   */
  stage?: { briefing_stage_id: string | null; name: string | null };
};

/** What one save wrote. */
export type StageZoneSaveResult = {
  /** festival_areas ids written, in write order (copied zones included). */
  ids: string[];
  /** The festival days (0-based) the save touched, ascending. */
  dayIndexes: number[];
  /** Pass to restoreStageZones to undo this save. */
  previous: StageZoneRestoreEntry[];
};

/** What one undo did. */
export type StageZoneRestoreResult = {
  /** Zones whose polygon was put back. */
  ids: string[];
  /** Zones removed (they were created by the save being undone). */
  deletedIds: string[];
  /**
   * Zones left as the save made them: their old outline breaks the polygon
   * rules (a legacy shape), or the save created them and crew presence has
   * been logged in them since.
   */
  skippedIds: string[];
  /** The festival days (0-based) the undo touched, ascending. */
  dayIndexes: number[];
  /** The polygons the undo replaced — a redo, for the zones it kept. */
  previous: StageZoneRestoreEntry[];
};

export type ZoneWriteError =
  /** The shape itself: same codes as validateZonePolygon, client or server. */
  | { kind: "invalid-polygon"; reason: ZonePolygonRejection; message: string }
  /** The server said no for another reason (access, foreign stage, history). */
  | { kind: "rejected"; message: string; code: string | null }
  /** The server answered with something this client does not understand. */
  | { kind: "unexpected-response"; message: string }
  /**
   * No answer from the server: offline, the request timed out, the
   * connection dropped, or a gateway in between gave up. The write may or
   * may NOT have happened — read the zone back before sending it again.
   */
  | { kind: "network"; message: string }
  /**
   * Not signed in (any more), or the session is not accepted: HTTP 401
   * (PostgREST's answer for a request without a valid user, 42501 included)
   * or PGRST301–303 (JWT). Not about the zone — a queued zone keeps waiting
   * for a working session. A 403 stays "rejected": that is a signed-in
   * user the server refuses (the RPCs raise 42501 "not authorised").
   */
  | { kind: "unauthorized"; message: string };

export type ZoneWriteResult<T> = { ok: true; value: T } | { ok: false; error: ZoneWriteError };

const RPC = "upsert_stage_zone_for_stage";

const restoreEntrySchema = z.object({
  area_id: z.string(),
  polygon: z.record(z.unknown()).nullable(),
  stage: z
    .object({ briefing_stage_id: z.string().nullable(), name: z.string().nullable() })
    .optional(),
});

const saveResponseSchema = z.object({
  ids: z.array(z.string()),
  day_indexes: z.array(z.number().int()),
  previous: z.array(restoreEntrySchema),
});

const restoreResponseSchema = saveResponseSchema.extend({
  deleted_ids: z.array(z.string()),
  skipped_ids: z.array(z.string()),
});

/**
 * The server's shape refusal, mapped back onto the client's codes. The
 * trigger raises SQLSTATE 22023 with "invalid <area_type> polygon: <code>:
 * <detail>" (assert_valid_zone_polygon). Null for any other error, and for
 * a 22023 whose message carries no known code. Exported for writers that
 * do not go through this module (the web zone editor).
 */
export function serverZonePolygonRejection(error: {
  code?: unknown;
  message?: unknown;
}): ZonePolygonRejection | null {
  if (error.code !== "22023" || typeof error.message !== "string") return null;
  const match = /polygon: ([a-z-]+):/.exec(error.message);
  const code = match?.[1];
  return ZONE_POLYGON_REJECTIONS.find((known) => known === code) ?? null;
}

/**
 * Gateways that answer for a server that did not (or not in time). Whether
 * the database committed is unknown, as with no answer at all.
 */
const GATEWAY_STATUSES: readonly number[] = [502, 503, 504];

/**
 * Did the request get no real answer? supabase-js reports a failed or
 * aborted fetch (no signal, the 15 s timeout) as status 0 with code "";
 * PostgREST's own errors always carry a code (PGRST…, or the SQLSTATE).
 */
const AUTH_CODES: readonly string[] = ["PGRST301", "PGRST302", "PGRST303"];

function isAuthFailure(error: { code?: unknown }, status: unknown): boolean {
  if (status === 401) return true;
  return typeof error.code === "string" && AUTH_CODES.includes(error.code);
}

function isNetworkFailure(error: { code?: unknown }, status: unknown): boolean {
  if (status === 0 || error.code === "") return true;
  return typeof status === "number" && GATEWAY_STATUSES.includes(status) && !error.code;
}

/**
 * A Supabase error as a ZoneWriteError. `status` is the HTTP status of the
 * response (0 when there was none). Shared by every zone writer.
 */
export function zoneWriteErrorOf(
  error: { code?: unknown; message?: unknown },
  status?: unknown,
): ZoneWriteError {
  const message = typeof error.message === "string" ? error.message : "Saving the zone failed";
  if (isNetworkFailure(error, status)) return { kind: "network", message };
  if (isAuthFailure(error, status)) return { kind: "unauthorized", message };
  const reason = serverZonePolygonRejection(error);
  if (reason !== null) return { kind: "invalid-polygon", reason, message };
  return { kind: "rejected", message, code: typeof error.code === "string" ? error.code : null };
}

const UNEXPECTED: ZoneWriteError = {
  kind: "unexpected-response",
  message: `${RPC} answered in an unexpected shape`,
};

/**
 * Rebuilt field by field rather than passed through: without
 * strictNullChecks (web, mobile) zod infers every key as optional, which
 * StageZoneRestoreEntry is not.
 */
function restoreEntriesOf(
  entries: ReadonlyArray<z.infer<typeof restoreEntrySchema>>,
): StageZoneRestoreEntry[] {
  return entries.map(({ area_id, polygon, stage }) => ({
    area_id,
    polygon,
    ...(stage ? { stage: { briefing_stage_id: stage.briefing_stage_id, name: stage.name } } : {}),
  }));
}

/**
 * Save `polygon` as the zone of the stage `briefingStageId` — on all its
 * festival days (default) or only on its own day.
 */
export async function saveStageZoneForStage(
  client: Client,
  args: {
    briefingId: string;
    /** briefing_stages.id of the stage on the day being edited. */
    briefingStageId: string;
    /** A GeoJSON Polygon, or a Feature wrapping one. */
    polygon: unknown;
    scope?: StageZoneScope;
  },
): Promise<ZoneWriteResult<StageZoneSaveResult>> {
  const validation = validateZonePolygon(args.polygon, "stage_zone");
  if (validation.ok === false) {
    return {
      ok: false,
      error: {
        kind: "invalid-polygon",
        reason: validation.reason,
        message: `The zone shape was refused: ${validation.reason}`,
      },
    };
  }

  const { data, error, status } = await client.rpc(RPC, {
    p_briefing_id: args.briefingId,
    p_briefing_stage_id: args.briefingStageId,
    // The server accepts a Feature too; storing the bare geometry keeps
    // every reader simple.
    p_polygon: zoneGeometryOf(args.polygon),
    p_scope: args.scope ?? "all_days",
  });
  if (error) return { ok: false, error: zoneWriteErrorOf(error, status) };

  const parsed = saveResponseSchema.safeParse(data);
  if (!parsed.success) return { ok: false, error: UNEXPECTED };
  return {
    ok: true,
    value: {
      ids: parsed.data.ids,
      dayIndexes: parsed.data.day_indexes,
      previous: restoreEntriesOf(parsed.data.previous),
    },
  };
}

/**
 * Undo a save: `restore` is the `previous` a save returned. One call, all
 * or nothing — except the zones reported in `skippedIds` (see the module
 * header). Removing a zone that is already gone is not an error, so an
 * undo retried after a dropped connection succeeds.
 */
export async function restoreStageZones(
  client: Client,
  args: { briefingId: string; restore: readonly StageZoneRestoreEntry[] },
): Promise<ZoneWriteResult<StageZoneRestoreResult>> {
  // Nothing to undo: no round trip, and no crew push for a no-op.
  if (args.restore.length === 0) {
    return {
      ok: true,
      value: { ids: [], deletedIds: [], skippedIds: [], dayIndexes: [], previous: [] },
    };
  }

  const { data, error, status } = await client.rpc(RPC, {
    p_briefing_id: args.briefingId,
    p_briefing_stage_id: null,
    p_polygon: null,
    p_restore: args.restore,
  });
  if (error) return { ok: false, error: zoneWriteErrorOf(error, status) };

  const parsed = restoreResponseSchema.safeParse(data);
  if (!parsed.success) return { ok: false, error: UNEXPECTED };
  return {
    ok: true,
    value: {
      ids: parsed.data.ids,
      deletedIds: parsed.data.deleted_ids,
      skippedIds: parsed.data.skipped_ids,
      dayIndexes: parsed.data.day_indexes,
      previous: restoreEntriesOf(parsed.data.previous),
    },
  };
}

/**
 * The outline the stage `briefingStageId` has on its own day right now, or
 * null when it has none — to tell, after a save whose answer was lost,
 * whether the server already holds it (see sameZoneOutline). An all-days
 * save writes this row too, so one read answers for either scope.
 */
export async function fetchStageZoneOutline(
  client: Client,
  args: { briefingId: string; briefingStageId: string },
): Promise<ZoneWriteResult<unknown | null>> {
  const { data, error, status } = await client
    .from("festival_areas")
    .select("polygon")
    .eq("briefing_id", args.briefingId)
    .eq("area_type", "stage_zone")
    .eq("briefing_stage_id", args.briefingStageId)
    .limit(1);
  if (error) return { ok: false, error: zoneWriteErrorOf(error, status) };
  const rows = Array.isArray(data) ? (data as Array<{ polygon?: unknown }>) : [];
  return { ok: true, value: rows[0]?.polygon ?? null };
}

/**
 * The outline each of the stage rows `briefingStageIds` has on its own day
 * (stage id → polygon; a stage without a zone is missing from the map) —
 * to confirm an ALL-DAYS save after a lost answer on every day it writes,
 * not only on the day it was drawn on.
 */
export async function fetchStageZoneOutlines(
  client: Client,
  args: { briefingId: string; briefingStageIds: readonly string[] },
): Promise<ZoneWriteResult<Map<string, unknown>>> {
  const { data, error, status } = await client
    .from("festival_areas")
    .select("briefing_stage_id, polygon")
    .eq("briefing_id", args.briefingId)
    .eq("area_type", "stage_zone")
    .in("briefing_stage_id", [...args.briefingStageIds]);
  if (error) return { ok: false, error: zoneWriteErrorOf(error, status) };
  const rows = Array.isArray(data)
    ? (data as Array<{ briefing_stage_id?: unknown; polygon?: unknown }>)
    : [];
  const outlines = new Map<string, unknown>();
  for (const row of rows) {
    if (typeof row.briefing_stage_id === "string") outlines.set(row.briefing_stage_id, row.polygon);
  }
  return { ok: true, value: outlines };
}

/**
 * Delete whole zones, for good — one statement for all of them, only rows
 * of this production (owner or active co-producer: festival_areas' FOR ALL
 * RLS policies). There is no undo: stage_presence_logs CASCADE, so the
 * attendance history of every deleted zone goes with it — the caller must
 * have said so before calling. Deleting a zone that is already gone is not
 * an error. Note: festival_areas' crew-refresh trigger is FOR EACH ROW, so
 * N rows deleted here send N silent crew pushes (no batch flag over REST).
 */
export async function deleteZones(
  client: Client,
  args: { briefingId: string; ids: readonly string[] },
): Promise<ZoneWriteResult<{ ids: string[] }>> {
  if (args.ids.length === 0) return { ok: true, value: { ids: [] } };
  const { error, status } = await client
    .from("festival_areas")
    .delete()
    .in("id", [...args.ids])
    .eq("briefing_id", args.briefingId);
  if (error) return { ok: false, error: zoneWriteErrorOf(error, status) };
  return { ok: true, value: { ids: [...args.ids] } };
}
